import {
  parseWeeklySchedule,
  type CustomFieldDefinition,
  type CustomFieldFilterValues,
  type CustomFieldValue,
} from '@erp/shared';
import { useEffect, useState } from 'react';
import { Filter, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { activeCustomFieldFilterCount } from '../lib/custom-field-filter';
import { CustomFieldControl } from './custom-field-control';

function isEmptyFilterValue(value: CustomFieldValue): boolean {
  return (
    value === null ||
    (typeof value === 'string' && value.trim().length === 0) ||
    (Array.isArray(value) && value.length === 0)
  );
}

function WeeklyScheduleFilterControl({
  definition,
  value,
  onChange,
}: {
  definition: CustomFieldDefinition;
  value: CustomFieldValue | undefined;
  onChange: (value: CustomFieldValue) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <CustomFieldControl
      definition={{ ...definition, isRequired: false }}
      value={draft}
      inputId={`custom-field-filter-${definition.key}`}
      onChange={(next) => {
        setDraft(next);
        if (next === null || parseWeeklySchedule(next) !== null) onChange(next);
      }}
    />
  );
}

export interface CustomFieldListFiltersProps {
  definitions: readonly CustomFieldDefinition[];
  value: CustomFieldFilterValues;
  onChange: (value: CustomFieldFilterValues) => void;
}

/** Type-aware list filters; the API independently revalidates visibility and filterability. */
export function CustomFieldListFilters({
  definitions,
  value,
  onChange,
}: CustomFieldListFiltersProps): JSX.Element | null {
  const { t } = useTranslation();
  const filterable = definitions.filter(
    (definition) =>
      definition.isActive && definition.isFilterable && definition.fieldType !== 'FORMULA',
  );
  const activeCount = activeCustomFieldFilterCount(value);

  if (filterable.length === 0) return null;

  const setValue = (key: string, next: CustomFieldValue): void => {
    if (isEmptyFilterValue(next)) {
      const copy = { ...value };
      delete copy[key];
      onChange(copy);
      return;
    }
    onChange({ ...value, [key]: next as CustomFieldFilterValues[string] });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant={activeCount > 0 ? 'default' : 'outline'} size="sm">
          <Filter className="h-4 w-4" aria-hidden="true" />
          {t('customFields.filters.action')}
          {activeCount > 0 ? <Badge variant="secondary">{activeCount}</Badge> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[min(70vh,36rem)] w-[min(24rem,calc(100vw-2rem))] overflow-y-auto"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">{t('customFields.filters.title')}</p>
            <p className="text-xs text-muted-foreground">{t('customFields.filters.description')}</p>
          </div>
          {activeCount === 0 ? null : (
            <Button variant="ghost" size="sm" onClick={() => onChange({})}>
              {t('customFields.filters.reset')}
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-4">
          {filterable.map((definition) => {
            const isSet = Object.prototype.hasOwnProperty.call(value, definition.key);
            return (
              <div key={definition.id} className="relative rounded-md border border-border p-3">
                {isSet ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1 z-10 h-7 w-7"
                    aria-label={t('customFields.filters.clearField', {
                      field: definition.label,
                    })}
                    onClick={() => {
                      const copy = { ...value };
                      delete copy[definition.key];
                      onChange(copy);
                    }}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                ) : null}
                {definition.fieldType === 'WEEKLY_SCHEDULE' ? (
                  <WeeklyScheduleFilterControl
                    definition={definition}
                    value={value[definition.key]}
                    onChange={(next) => setValue(definition.key, next)}
                  />
                ) : (
                  <CustomFieldControl
                    definition={{ ...definition, isRequired: false }}
                    value={value[definition.key]}
                    inputId={`custom-field-filter-${definition.key}`}
                    onChange={(next) => setValue(definition.key, next)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
